import { apiClient } from '@/api/client';
import {
  AdminUserCreateDTORole,
  AdminUserUpdateDTORole,
  User,
  UserEventCreateDTOEntityType,
  UserEventCreateDTOEventType,
} from '@/api/models';
import { useNotifications } from '@/lib/notifications';
import { queryKeys } from '@/lib/queryKeys';
import { useTrackEventSimple } from '@/lib/trackingService';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

export interface UserFormData {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  role: AdminUserCreateDTORole;
  enabled: boolean;
}

export interface UserMutationsActions {
  handleFormSubmit: (
    formData: UserFormData,
    drawerMode: 'view' | 'create' | 'edit',
    selectedUser: User | null,
  ) => Promise<void>;
  handleDeleteUser: (user: User) => void;
}

export const useUserMutations = () => {
  const { addNotification } = useNotifications();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { trackEvent } = useTrackEventSimple();

  // Create user mutation
  const createUserMutation = useMutation({
    mutationFn: async (userData: UserFormData) => {
      const response = await apiClient.POST('/admin/users', {
        body: {
          email: userData.email,
          firstName: userData.firstName,
          lastName: userData.lastName,
          phone: userData.phone,
          password: 'TempPassword123!',
          role: userData.role,
        },
      });

      if (response.error) {
        throw new Error('Failed to create user');
      }

      return response.data;
    },
    onSuccess: () => {
      addNotification({
        type: 'success',
        title: 'Success',
        message: 'User created successfully',
      });
      queryClient.invalidateQueries({ queryKey: [queryKeys.ADMIN_USERS] });
    },
    onError: (error: Error) => {
      addNotification({
        type: 'error',
        title: 'Error',
        message: error.message || 'Failed to create user',
      });
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: async (
      userData: UserFormData & { id: number; originalUser?: User },
    ) => {
      const response = await apiClient.PUT('/admin/users/{id}', {
        params: {
          path: { id: userData.id },
        },
        body: {
          firstName: userData.firstName,
          lastName: userData.lastName,
          phone: userData.phone,
          role: AdminUserUpdateDTORole[userData.role],
          subject: 'system',
          enabled: userData.enabled,
        },
      });

      if (response.error) {
        throw new Error('Failed to update user');
      }

      // Calculate changed fields for tracking
      const changedFields: Record<string, string | number | boolean> = {};
      const original = userData.originalUser;
      if (original) {
        if (userData.firstName !== original.firstName)
          changedFields.firstName = userData.firstName;
        if (userData.lastName !== original.lastName)
          changedFields.lastName = userData.lastName;
        if (userData.phone !== original.phone)
          changedFields.phone = userData.phone;
        if (userData.enabled !== original.enabled)
          changedFields.enabled = userData.enabled;
      }

      return {
        data: response.data,
        id: userData.id,
        enabled: userData.enabled,
        previousEnabled: original?.enabled,
        changedFields,
      };
    },
    onSuccess: result => {
      if (
        result.previousEnabled !== undefined &&
        result.previousEnabled !== result.enabled
      ) {
        trackEvent({
          eventType: UserEventCreateDTOEventType.admin_user_status_changed,
          entityType: UserEventCreateDTOEntityType.user,
          entityId: result.id,
          properties: {
            enabled: result.enabled,
          },
        });
      } else {
        trackEvent({
          eventType: UserEventCreateDTOEventType.admin_user_updated,
          entityType: UserEventCreateDTOEntityType.user,
          entityId: result.id,
          properties: result.changedFields,
        });
      }
      addNotification({
        type: 'success',
        title: 'Success',
        message: 'User updated successfully',
      });
      queryClient.invalidateQueries({ queryKey: [queryKeys.ADMIN_USERS] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.ADMIN_USER] });
    },
    onError: (error: Error) => {
      addNotification({
        type: 'error',
        title: 'Error',
        message: error.message || 'Failed to update user',
      });
    },
  });

  // Delete user mutation
  const deleteUserMutation = useMutation({
    mutationFn: async (userId: number) => {
      const response = await apiClient.DELETE('/admin/users/{id}', {
        params: {
          path: { id: userId },
        },
      });

      if (response.error) {
        throw new Error('Failed to delete user');
      }

      return { success: true };
    },
    onSuccess: () => {
      addNotification({
        type: 'success',
        title: 'Success',
        message: 'User deleted successfully',
      });
      queryClient.invalidateQueries({ queryKey: [queryKeys.ADMIN_USERS] });
    },
    onError: (error: Error) => {
      addNotification({
        type: 'error',
        title: 'Error',
        message: error.message || 'Failed to delete user',
      });
    },
  });

  const handleFormSubmit = async (
    formData: UserFormData,
    drawerMode: 'view' | 'create' | 'edit',
    selectedUser: User | null,
  ) => {
    try {
      if (drawerMode === 'create') {
        await createUserMutation.mutateAsync(formData);
      } else if (drawerMode === 'edit' && selectedUser && selectedUser.id) {
        await updateUserMutation.mutateAsync({
          ...formData,
          id: selectedUser.id,
          originalUser: selectedUser,
        });
      }
      navigate('/users');
    } catch {
      // Error handling is done in the mutations
    }
  };

  const handleDeleteUser = (user: User) => {
    if (
      window.confirm(
        `Are you sure you want to delete ${user.firstName} ${user.lastName}?`,
      )
    ) {
      deleteUserMutation.mutate(user.id!);
    }
  };

  const actions: UserMutationsActions = {
    handleFormSubmit,
    handleDeleteUser,
  };

  const isFormLoading =
    createUserMutation.isPending || updateUserMutation.isPending;
  const isDeleting = deleteUserMutation.isPending;

  return {
    actions,
    isFormLoading,
    isDeleting,
  };
};
